import type { NationId } from "@cwb/core";
import { useGameStore } from "../stores/gameStore";
import type { GameStoreState } from "../stores/gameStore";

/**
 * The only seam between React and Phaser (§3.3).
 *
 * Phaser never imports React and React never imports Phaser. Both talk to this
 * object: the scene subscribes to store snapshots and pushes selections back,
 * React sends highlight cues the other way. The camera belongs to the player —
 * nothing here drives it.
 */

type BridgeEventMap = {
  /**
   * Flash a nation's border: an agent issuing a visible order, or the player
   * picking that nation in a panel.
   */
  pulseNation: { nationId: NationId };
  /** Draw attention to a convoy the player just created. */
  traceShipment: { shipmentId: string };
};

type Listener<K extends keyof BridgeEventMap> = (payload: BridgeEventMap[K]) => void;

class Emitter {
  private listeners = new Map<keyof BridgeEventMap, Set<Listener<never>>>();

  on<K extends keyof BridgeEventMap>(event: K, listener: Listener<K>): () => void {
    let bucket = this.listeners.get(event);
    if (!bucket) {
      bucket = new Set();
      this.listeners.set(event, bucket);
    }
    bucket.add(listener as Listener<never>);
    return () => {
      bucket?.delete(listener as Listener<never>);
    };
  }

  emit<K extends keyof BridgeEventMap>(event: K, payload: BridgeEventMap[K]): void {
    const bucket = this.listeners.get(event);
    if (!bucket) return;
    for (const listener of bucket) (listener as Listener<K>)(payload);
  }

  clear(): void {
    this.listeners.clear();
  }
}

const emitter = new Emitter();

export const gameBridge = {
  /** Current store snapshot — the scene reads this every frame it needs to. */
  getState(): GameStoreState {
    return useGameStore.getState();
  },

  /** Store subscription for the scene's update loop. */
  subscribe(listener: (state: GameStoreState) => void): () => void {
    return useGameStore.subscribe(listener);
  },

  // ---- Phaser -> React ----
  selectProvince(provinceId: string | null): void {
    useGameStore.getState().selectProvince(provinceId);
  },

  selectNation(nationId: NationId | null): void {
    useGameStore.getState().selectNation(nationId);
  },

  /**
   * What the pointer is over.
   *
   * The scene has always known this — it drives the province highlight — but
   * kept it to itself. React needs it now that a nation's details are read by
   * hovering the map rather than from a panel, and it comes from here rather
   * than from a DOM mousemove because the canvas is the only thing that knows
   * which polygon a pixel belongs to.
   *
   * It used to carry the cursor position as well, for a dossier that hung off
   * the pointer. That dossier is docked along the foot of the map now, so the
   * only thing that moves is which province is named.
   *
   * Called on every pointer move, so it must stay a no-op when nothing changed:
   * the store setter compares before it writes.
   */
  hoverProvince(provinceId: string | null): void {
    useGameStore.getState().setHoveredProvince(provinceId);
  },

  // ---- React -> Phaser ----
  pulseNation(nationId: NationId): void {
    emitter.emit("pulseNation", { nationId });
  },

  traceShipment(shipmentId: string): void {
    emitter.emit("traceShipment", { shipmentId });
  },

  on<K extends keyof BridgeEventMap>(event: K, listener: Listener<K>): () => void {
    return emitter.on(event, listener);
  },

  dispose(): void {
    emitter.clear();
  },
};

export type GameBridge = typeof gameBridge;
